<template>
  <div
    class="convey911-header border-bottom"
    :class="{
      'dark': isDarkMode,
    }"
    @mouseenter="mouseEnter"
  >
    <div class="convey911-header--top d-flex align-items-center justify-content-between">
      <div class="convey911-header--nav d-flex align-items-center pl-3" style="flex: 1;">
        <div class="convey911-nav-item">
          <div
            v-if="!['Home', 'Page Not Found', 'Unknown Page'].includes($route.name)"
            class="convey911-sidebar-trigger"
            v-indicator="{ show: sidebarIndicator || !userSidebarOpened }"
            :class="{ dark: isDarkMode }"
            @click="toggleSidebar"
          >
            <SidebarOpen v-if="!userSidebarOpened" />
            <SidebarClose v-else />
          </div>
          <div v-else class="">
            <router-link to="/">
              <div class="d-flex align-items-center">
                <img src="/images/rcl-logo-badge-50.png" alt="homepage" />
                <img v-if="!isMobile" src="/images/Convey911_logo_text-145x25.png" alt="homepage" />
              </div>
            </router-link>
          </div>
        </div>
        <div class="d-none d-xl-block pl-4 pr-2" style="flex: 1;">
          <ModuleNavigator :isDarkMode="isDarkMode" />
        </div>
      </div>
      <div class="convey911-header--nav d-flex align-items-center justify-content-between">
        <div class="convey911-nav-items--left d-flex align-items-center">
        </div>
        <div class="convey911-nav-items--right d-flex align-items-center justify-content-end pr-2 pr-md-3">
          <div v-if="!isMobile && !hideAgencyPicker" class="convey911-nav-item d-flex">
            <AgencyPicker />
          </div>
          <div class="convey911-nav-item">
            <ConnectionStatus :connected="connected" />
          </div>
          <div
            v-if="connected && currentAgency"
            class="convey911-nav-item create-new"
            :class="{ dark: isDarkMode }"
          >
            <b-dropdown
              id="create-new-dropdown"
              class="create-new-dropdown"
              v-indicator="{ show: showCreateNewIndicator, rounded: true }"
              @toggle="() => { showCreateNewIndicator = false }"
            >
              <template #button-content>
                <b-icon-plus-circle />
              </template>
              <b-dropdown-item
                v-if="agencySettings['core:communicatorEnabled'] && $permission('create:conversation')"
                class="create-new-dropdown--item"
                @click="newConversation()"
              >
                <b-icon-chat-left-text class="mr-3" />
                <span>New Conversation</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="agencySettings.mediaProcessorEnabled"
                class="create-new-dropdown--item"
                @click="newMediaFile()"
              >
                <b-icon-file-earmark-play class="mr-3" />
                <span>New Media File</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="agencySettings['core:alertEnabled']"
                class="create-new-dropdown--item"
                @click="newNotification()"
              >
                <b-icon-people class="mr-3" />
                <span>New Campaign Event</span>
              </b-dropdown-item>
            </b-dropdown>
            <b-popover
              target="create-new-dropdown"
              :show="userCreateNewPoppver"
              placement="bottom"
            >
              Click this button to create new <b>Conversation</b>, <b>Media File</b>, <b>Campaign Event</b>.
            </b-popover>
          </div>
          <!-- <div class="convey911-nav-item get-support">
            <b-button variant="secondary" @click="getSupport">
              <b-icon-question-circle />
            </b-button>
          </div> -->
          <div v-if="pendingInvitations.length > 0" class="convey911-nav-item join-agency">
            <b-button variant="secondary" @click="openJoinAgencyModal">
              <b-icon-people-fill />
              <b-badge variant="danger">
                {{ pendingInvitations ? pendingInvitations.length : 0 }}
              </b-badge>
            </b-button>
          </div>
          <div class="convey911-nav-item volume" :class="{ dark: isDarkMode }">
            <b-dropdown class="volume-control-dropdown">
              <template #button-content>
                <VolumeXIcon v-if="formattedVolume === 0" size="24"></VolumeXIcon>
                <VolumeIcon v-else-if="formattedVolume > 0 && formattedVolume < 11" size="24">
                </VolumeIcon>
                <Volume1Icon v-else-if="formattedVolume > 10 && formattedVolume < 68" size="24">
                </Volume1Icon>
                <Volume2Icon v-else size="24"></Volume2Icon>
              </template>
              <b-dropdown-header>
                <VolumeController />
              </b-dropdown-header>
            </b-dropdown>
          </div>
          <div class="convey911-nav-item theme">
            <LightDarkModeSelector :showLabel="false" />
          </div>
          <div class="convey911-nav-item profile-dropdown-container">
            <ProfileDropDown @logout="logout" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="isMobile" class="convey911-header--bottom d-flex align-items-center justify-content-center px-2">
      <div v-if="!hideAgencyPicker" class="convey911-nav-item d-block convey911-header--agency-picker">
        <AgencyPicker />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import ProfileDropDown from "./ProfileDropDown";
import AgencyPicker from "./AgencyPicker";
import AgencyBanner from "./AgencyBanner";
import { EventBus } from "../events/eventBus";
import { mapGetters, mapState } from "vuex";
import NewConversationForm from "./conversations/NewConversationForm";
import ConnectionStatus from "./ConnectionStatus";
import NewRecordingForm from "./conversations/NewRecordingForm";
import GroupNotificationForm from "./campaigns/GroupNotificationForm";
// import GetSupport from "@/components/GetSupport";
import AcceptInvitation from "@/components/AcceptInvitation";
import VolumeController from "@/components/VolumeController";
import LightDarkModeSelector from "@/components/LightDarkModeSelector";
import { Volume1Icon, Volume2Icon, VolumeXIcon, VolumeIcon } from "vue-feather-icons";
import SidebarOpen from "@/components/icons/SidebarOpen";
import SidebarClose from "@/components/icons/SidebarClose";
import ModuleNavigator from "@/components/ModuleNavigator";
import ModuleNavigatorPicker from "@/components/ModuleNavigatorPicker";

export default {
  name: "TopBar",
  props: {
    sidebarIndicator: Boolean,
  },
  components: {
    ProfileDropDown,
    AgencyPicker,
    AgencyBanner,
    ConnectionStatus,
    VolumeController,
    LightDarkModeSelector,
    Volume1Icon,
    Volume2Icon,
    VolumeXIcon,
    VolumeIcon,
    SidebarOpen,
    SidebarClose,
    ModuleNavigator,
    ModuleNavigatorPicker,
  },
  data() {
    return {
      showCreateNewIndicator: true,
    };
  },
  computed: {
    ...mapGetters("agency", ["currentAgency", "availableAgents", "agencySettings"]),
    ...mapGetters("conversations", ["conversationStatus"]),
    ...mapGetters("user", [
      "currentUser",
      "isMobile",
      "currentToken",
      "currentSession",
      "volume",
      "isDarkMode",
      "userSidebarOpened",
      "userCreateNewPoppver"
    ]),
    ...mapState({
      connected: (state) => state.connection.connected,
      sessionId: (state) => state.user.session,
      currentToken: (state) => state.user.token,
      showGetSupportModal: state => state.user.showGetSupportModal,
      pendingInvitations: state => state.user.pendingInvitations,
    }),
    availableAgentsByName() {
      return [...new Map(this.availableAgents.map(agent => [agent.agentId, agent])).values()];
    },
    hideAgencyPicker() {
      return this.$auth.isloading
        || !this.$auth.isAuthenticated
        || !this.currentUser
        || (
          this.currentUser
          && this.currentUser.agencies
          && this.currentUser.agencies.length === 0
        );
    },
    formattedVolume() {
      return Math.ceil(this.volume * 100);
    },
  },
  created() {
    this.$store.commit("user/setUserCreateNewPoppver", true);
    setTimeout(() => {
      this.$store.commit("user/setUserCreateNewPoppver", false);
    }, 5000);
  },
  methods: {
    login() {
      this.$auth.loginWithRedirect();
    },
    async logout() {
      try {
        await this.$ConveyApi.sessionsApi.deleteSession({ sessionId: this.currentSession });
      } catch (error) {
        console.error(`Error deleting session ${error}`);
      } finally {
        this.$auth.logout({ returnTo: window.location.origin });
      }
    },
    newConversation() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: NewConversationForm,
        title: "New Conversation",
        actionButton: "Start Conversation",
        closeOnClick: false,
      });
    },
    newNotification() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: GroupNotificationForm,
        title: "New Campaign Event",
        actionButton: "Create Campaign Event",
        closeOnClick: false,
      });
    },
    newMediaFile() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: NewRecordingForm,
        title: "New Media File",
        actionButton: "Interpret Voice Recording",
        closeOnClick: false,
      });
    },
    // getSupport() {
    //   EventBus.$emit("open-get-support-modal");
    // },
    openJoinAgencyModal() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static",
      });
      EventBus.$emit("open", {
        component: AcceptInvitation,
        title: "Invitation to Join Agency",
        closeOnClick: false,
        props: {},
      });
    },
    toggleSidebar() {
      this.$store.commit("user/setUserSidebarOpened", !this.userSidebarOpened);
    },
    mouseEnter($event) {
      this.$store.commit("user/setUserCreateNewPoppver", false);
    }
  }
};
</script>

<style lang="scss">
.convey911-header {
  width: 100%;
  height: 100%;
  background-color: #e9edf2 !important;
  &.dark {
    background-color: #263238 !important;
  }

  &--top {
    width: 100%;
    height: 70px;

    @media screen and (max-width: 767px) {
      height: 60px;
    }
  }


  &--agency-picker {
    width: 100% !important;

    & .agency-picker {
      width: 100%;

      & .agency-picker--content {
        width: 100%;
      }
    }
  }


  &--bottom {
    width: 100%;
    height: 0;

    @media screen and (max-width: 767px) {
      height: 40px;
    }
  }

  &--logo {
    height: 100%;
    cursor: pointer;
    @media screen and (min-width: 767px) {
      min-width: 330px;
    }
  }

  &--nav {
    height: 100%;

    .convey911-nav-items--right {
      height: 100%;
      gap: 1rem;

      .convey911-nav-item {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;

        &.create-new {
          .create-new-dropdown {
            >button {
              display: flex;
              justify-content: center;
              align-items: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #455a64;
              background-color: transparent !important;
              border: none;
              padding: 0;

              >svg {
                font-size: 100% !important;
              }

              &::after {
                display: none;
              }
            }

            &--item {
              display: flex;
              align-items: center;
            }
          }

          &.dark {
            .create-new-dropdown {
              >button {
                color: #b2b9bf;
              }
            }
          }
        }

        &.volume {
          .volume-control-dropdown {
            >button {
              display: flex;
              justify-content: center;
              align-items: center;
              color: #455a64;
              background-color: transparent !important;
              border: none;
              padding: 0;

              &::after {
                display: none;
              }
            }

            &--item {
              display: flex;
              align-items: center;
            }
          }

          &.dark {
            .volume-control-dropdown {
              >button {
                color: #b2b9bf;
              }
            }
          }
        }

        &.get-support {
          >button {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 1.5rem;
            font-weight: bold;
            color: #455a64;
            background-color: transparent !important;
            border: none;
            padding: 0;

            &::after {
              display: none;
            }
          }
        }

        &.join-agency {
          >button {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 1.5rem;
            font-weight: bold;
            color: #455a64;
            background-color: transparent !important;
            border: none;
            padding: 0;
            position: relative;

            >svg {
              font-size: 100% !important;
            }

            &::after {
              display: none;
            }

            >span.badge {
              position: absolute;
              top: -5px;
              right: -10px;
              border-radius: 10px;
              font-size: 10px;
              padding: 2px 4px;
            }

            @media screen and (max-width: 767px) {
              font-size: 1rem;

              >span.badge {
                top: -4px;
                right: -6px;
                font-size: 8px;
              }
            }
          }
        }

        &.profile-dropdown-container {
          width: 50px;
        }
      }

      @media screen and (max-width: 767px) {
        gap: 8px;
      }
    }
  }

  .convey911-sidebar-trigger {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    color: #455a64;
    background-color: transparent !important;
    border: none;
    padding: 0;
    cursor: pointer;

    &.dark {
      color: #b2b9bf;
    }
  }
}
</style>